import React from 'react';
import Select, { components } from 'react-select';
import styled, { useTheme } from 'styled-components/macro';
import { last } from 'lodash';
import { CheckboxInput } from 'lib/components';
import { useTranslation } from 'react-i18next';
import { getSecondaryColor } from 'lib/context/theme/utils';

const MenuHeader = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 15px;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.black[100]};
  padding: 8px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[20]};
  cursor: pointer;
`;

const MenuList = (props: any) => {
  const { t } = useTranslation();
  return (
    <components.MenuList {...props}>
      <MenuHeader
        onClick={() => {
          props.selectProps.onChange([
            {
              value: 'all',
              label: t('All Departments'),
            },
          ]);
        }}
      >
        {t('All Departments')}
      </MenuHeader>
      {props.children}
    </components.MenuList>
  );
};

const OptionWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  cursor: pointer;
  > label {
    font-style: normal;
    font-weight: 600;
    font-size: 15px;
    line-height: 24px;
    color: ${({ theme }) => theme.colors.black[100]};
    margin-left: 8px;
    cursor: pointer;
    width: 168px;
    text-align: left;
    text-overflow: ellipsis;
    overflow: hidden;
    text-wrap: nowrap;
  }
`;

const Option = (props: any) => {
  return (
    <div>
      <components.Option {...props}>
        <OptionWrapper>
          <CheckboxInput
            checked={props.isSelected}
            onChange={() => null}
            onClick={(event: React.MouseEvent<HTMLLabelElement>) => {
              event.preventDefault();
            }}
          />
          <label title={props.label}>{props.label}</label>
        </OptionWrapper>
      </components.Option>
    </div>
  );
};

const ValueContainer = ({ children, ...props }: any) => {
  const { t } = useTranslation();
  const values = props.getValue();

  // to enable closing on outside click
  const selectOptionsAndInput = React.Children.map(children, child => {
    if (child && [components.SingleValue].indexOf(child.type) === -1) {
      return child;
    }
    return null;
  });

  let searchInput = last(selectOptionsAndInput);

  let text = t('All Departments');
  if (values.length) {
    if (values[0].label.trim().length > 15) {
      text = `${values[0].label.trim().substring(0, 15) + '...'}`;
    } else {
      text = `${values[0].label}`;
    }
  }
  if (values.length > 1)
    text = t('{{count}} departments', { count: values.length });

  return (
    <components.ValueContainer {...props}>
      <div
        style={{
          maxHeight: 40,
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        <div>{text}</div>
        {searchInput as React.ReactNode}
      </div>
    </components.ValueContainer>
  );
};

type Department = {
  label: string;
  value: string;
};

type Props = {
  onDepartmentsSelected: (values: string[]) => void;
  departments: Department[];
  selectInputWidth?: string;
  showMenu?: boolean;
  setShowMenu?: (arg: boolean) => void;
  menuPortalTarget?: HTMLElement | null;
  menuShouldBlockScroll?: boolean;
};

export const DepartmentsCheckboxDropdown = ({
  onDepartmentsSelected,
  departments,
  selectInputWidth = '225px',
  menuPortalTarget = document.body,
  menuShouldBlockScroll = true,
}: Props) => {
  const { t } = useTranslation();
  const themes = useTheme();
  const [selectedDepartments, setSelectedDepartments] = React.useState(
    [] as Department[]
  );

  const onChangeDepartments = (newValues: Department[]) => {
    newValues = newValues || [];
    if (newValues[0]?.value === 'all') {
      setSelectedDepartments(departments);
      onDepartmentsSelected(departments?.map(value => value.value));
      return;
    }
    setSelectedDepartments(newValues);
    onDepartmentsSelected(newValues.map(value => value.value));
  };

  return (
    <Select
      placeholder={t('All Departments')}
      options={departments}
      isMulti
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      menuShouldBlockScroll={menuShouldBlockScroll}
      menuPortalTarget={menuPortalTarget}
      menuPosition='absolute'
      isClearable={false}
      isSearchable={false}
      components={{
        Option,
        ValueContainer,
        IndicatorSeparator: () => null,
        MenuList,
      }}
      // @ts-ignore
      onChange={value => onChangeDepartments(value)}
      value={selectedDepartments}
      styles={{
        menu: (base: any) => ({
          ...base,
          width: 'fit-content',
          right: 0,
          zIndex: 999,
        }),
        menuList: (base: any) => ({
          ...base,
          width: '100%',
          overflow: 'auto',
          maxHeight: '220px',
          marginBottom: '10px',
        }),
        menuPortal: (base: any) => ({
          ...base,
          zIndex: 9999,
        }),
        option: (base: any) => ({
          ...base,
          cursor: 'pointer',
          background: 'transparent',
          display: 'flex',
          justifyContent: 'flex-start',
          '&:hover': {
            background: 'transparent',
          },
          '&:active': {
            background: 'transparent',
          },
        }),
        control: (base: any) => ({
          ...base,
          border: `1px solid ${themes.colors.neutral[40]}`,
          height: '40px',
          width: selectInputWidth,
          boxShadow: 'none',
          '&:hover': {
            border: `1px solid ${getSecondaryColor(themes)}}`,
          },
        }),
      }}
    />
  );
};
